<script>
import EditableTable from '@/components/EditableTable'
import Button from '@/components/Button'
import FloatingLabelTextInput from '@/components/FloatingLabelTextInput'
import LabelledSeparator from '@/components/LabelledSeparator'
import DropdownSelect from '@/components/DropdownSelect'
import CitiesServices from '@/services/cities'
import StoresServices from '@/services/stores'
import AccountsServices from '@/services/accounts'
import DialogUtils from '@/utils/dialogUtils'
import ArrayUtils from '@/utils/arrayUtils'
import ObjectUtils from '@/utils/objectUtils'
import EnvironmentUtils from '@/utils/environmentUtils'
import StringUtils from '@/utils/stringUtils'
import DOMUtils from '@/utils/DOMUtils'
import { waitUntilUserIsFetched } from '@/stores/index'
import Vue from 'vue'

export default
{
    name: 'accounts-management',

    created ()
    {
        this.$refs.table = {}

        const features = EnvironmentUtils.cityFeatures
        if (!features.hasReports) delete this.tableData.labels.canManageReports
        if (!features.hasStores)
        {
            delete this.tableData.labels.canEditStores
            delete this.tableData.labels.manageStore
        }
        if (!features.hasLostObjects) delete this.tableData.labels.canManageLostObjects
        if (!features.hasLostPets) delete this.tableData.labels.canManageLostPets
    },

    render () 
    {
        const isAddExistingAccountButtonDisabled = StringUtils.isEmptyString(this.addExistingAccountName)

        return (
            <div id='accounts-management-view'>
                <div id='add-accounts-zone'>
                    <Button id='create-account-btn' text='Créer un nouveau compte' type='secondary' icon={require('@/assets/img/plus.svg')}
                        on-click={this.createAccountClicked} />
                    <LabelledSeparator label='ou' orientation={DOMUtils.reactives.currentMediaName == 'mobile' ? 'horizontal' : 'vertical'} />
                    <div id='add-existing-account'>
                        <div class='title'>Ajouter un compte déjà existant</div>
                        <div id='input-controls'>
                            <FloatingLabelTextInput placeholder='Identifiant du compte' autocapitalize='none' v-model={this.addExistingAccountName}
                                ref='addExistingAccountInput' on-enter={this.addExistingAccount} />
                            <Button text='Ajouter' type='light' disabled={isAddExistingAccountButtonDisabled} on-click={this.addExistingAccountClicked} />
                        </div>
                    </div>
                </div>
                <EditableTable ref='table' labels={this.tableData.labels} v-model={this.tableData.values} />
                <Button id='save-btn' class='has-no-border' v-show={this.$refs.table.hasBeenModified} text='Enregistrer les modifications'
                    type='primary' on-click={this.saveChangesClicked} />
            </div>
        )
    },

    data () 
    {
        return {
            addExistingAccountName: '',
            tableData:
            {
                labels:
                {
                    username: 'Identifiant', canEditActualities: 'Éditer les actualités', canEditEvents: 'Éditer les événements',
                    canEditUsefulInfos: 'Éditer les informations utiles', canManageReports: 'Gérer les signalements', canEditStores: 'Éditer les commerces',
                    canManageAccounts: 'Gérer les comptes', canManageNotifications: 'Gérer les notifications', canManageLostObjects: 'Gérer les objets perdus',
                    canManageLostPets: 'Gérer les animaux perdus',
                    manageStore: {name: 'Gère le commerce', type: 'input', placeholder: 'Nom du commerce', autocomplete: Vue.observable([])}, isAdministrator: 'Administrateur'
                },
                values: []
            }
        }
    },

    computed:
    {
        stores ()
        {
            return this.$store.state.stores.storeList
        },

        cityAdministrators ()
        {
            return this.$store.getters.cities.getCityAdministratorsById(EnvironmentUtils.cityId)
        }
    },

    watch:
    {
        '$store.state.stores.storeList' ()
        {
            this.refreshManageStoreAutocomplete()
        }
    },

    mounted ()
    {
        this.refreshManageStoreAutocomplete()
        
        this.fillTable()
    },

    methods:
    {
        async addExistingAccountClicked (event, button)
        {
            button.showSpinner = true

            await this.addExistingAccount()

            button.showSpinner = false
        },

        async addExistingAccount ()
        {
            try
            {
                const cityId = EnvironmentUtils.cityId
                const accountInfo = await AccountsServices.searchAccount(this.addExistingAccountName, cityId)
                const uId = accountInfo.id
                const token = this.$store.state.user.token
                const updatedPrivileges = await CitiesServices.setUserPrivileges(cityId, token, uId, { isAdministrator: true })
                await this.$store.dispatch('cities/fetchAdministratorAccount', { cityId, token, accountId: uId })
                this.fillTable()

                console.log('Added user', accountInfo, 'as administrator with privileges', updatedPrivileges)
                this.addExistingAccountName = ''
            }
            catch (error)
            {
                if (error.response.status == 404)
                {
                    this.$refs.addExistingAccountInput.setErrorMessage('Ce compte n\'existe pas')
                }
                else throw error
            }
        },

        async fillTable ()
        {
            if (ObjectUtils.isEmpty(this.cityAdministrators))
            {
                console.log('No local city administrators, fetching it...')
                await waitUntilUserIsFetched()
                const cityId = EnvironmentUtils.cityId
                const token = this.$store.state.user.token

                const admins = await CitiesServices.getCityAdministrators(cityId, token)
                for (const accountId of admins)
                {
                    await this.$store.dispatch('cities/fetchAdministratorAccount', { cityId, token, accountId })
                }

                if (!ObjectUtils.isEmpty(admins)) return this.fillTable()
            }

            StoresServices.fetchStores(EnvironmentUtils.cityId).then(stores =>
            {
                this.$store.commit('stores/updateStoreList', stores)
            })
            this.$refs.table.clearTable()

            const cityAdminsSorted = Object.entries(this.cityAdministrators).sort((entry1, entry2) => entry1[1].username.localeCompare(entry2[1].username))
            for (const [userId, user] of cityAdminsSorted)
            {
                const ownedStore = this.stores.find(s => s.ownedBy == userId)
                const row = { username: user.username, ...user.privileges, manageStore: (ownedStore || {}).name }

                this.$refs.table.addRow(row)
            }

            this.$refs.table.initialized()
        },

        createAccountClicked ()
        {
            this.$router.push({ path: 'create', append: true })
        },

        async saveChangesClicked (event, button)
        {
            button.showSpinner = true

            const cityAdministrators = this.$store.getters.cities.getCityAdministratorsById(EnvironmentUtils.cityId)
            const getAdminIdByUsername = username =>
            {
                for (const adminIndex in cityAdministrators)
                {
                    if (cityAdministrators[adminIndex].username == username)
                    {
                        return adminIndex
                    }
                }
            }

            const loggedUserUsername = this.$store.state.user.username
            let modifiedRows = this.$refs.table.getModifiedRows()
            const loggedUserRow = modifiedRows.find(r => r[0] == loggedUserUsername)
            // if user changes his own privileges, that must be proceeded last.
            if (loggedUserRow)
            {
                modifiedRows = ArrayUtils.modifyIndex(modifiedRows, modifiedRows.indexOf(loggedUserRow), modifiedRows.length - 1)
            }

            for (const row of modifiedRows)
            {
                const rowIndex = this.tableData.values.findIndex(r => r[0] == row[0])
                const labelled = this.$refs.table.getLabelledRow(rowIndex)
                let adminId = getAdminIdByUsername(labelled.username)

                if (labelled.username == loggedUserUsername && !labelled.canManageAccounts)
                {
                    const confirmed = await DialogUtils.waitForUserConfirmationAsync
                    ({
                        description: `Vous êtes sur le point de retirer à l'administrateur "${labelled.username}" (vous-même) le droit de gérer les comptes, vous ne pourrez donc plus revenir sur cette page par la suite.\n\nVoulez vous continuer ?`,
                    })

                    if (!confirmed) continue
                }

                console.log('Saving administrator', adminId, 'privileges to', labelled)
                let updatedPrivileges = await CitiesServices.setUserPrivileges(EnvironmentUtils.cityId, this.$store.state.user.token, adminId, labelled)
                this.$store.commit('cities/updateCityAdministrators', { cityId: EnvironmentUtils.cityId, administrators: {[adminId]: { privileges: updatedPrivileges } }})

                const setStoreOwner = async (storeId, ownerId) => 
                {
                    const res = await StoresServices.updateStore({id: storeId, ownedBy: ownerId}, EnvironmentUtils.cityId, this.$store.state.user.token)
                    this.$store.commit('stores/updateStoreList', [res])
                }

                if (labelled.manageStore)
                {
                    let store = this.stores.find(s => s.name == labelled.manageStore)
                    if (!store)
                    {
                        this.$toast.error(`Le compte ${labelled.username} ne peut pas gérer le commerce "${labelled.manageStore}" car ce commerce n'existe pas.`, {duration: 10000})
                    }
                    else if (store.ownedBy != adminId)
                    {
                        const oldOwnedStore = this.stores.find(s => s.ownedBy == adminId)
                        if (oldOwnedStore)
                        {
                            await setStoreOwner(oldOwnedStore.id, -1)
                        }
                        
                        await setStoreOwner(store.id, adminId)
                    }
                }
                else
                {
                    const oldOwnedStore = this.stores.find(s => s.ownedBy == adminId)
                    if (oldOwnedStore)
                    {
                        console.log('Removing account', adminId, 'ownership over store', oldOwnedStore)
                        await setStoreOwner(oldOwnedStore.id, -1)
                    }
                }
            }

            this.fillTable()

            button.showSpinner = false
        },

        refreshManageStoreAutocomplete ()
        {
            if (!this.tableData.labels.manageStore) return
            this.tableData.labels.manageStore.autocomplete = this.stores.map(s => s.name)
        }
    },

    components:
    {
        EditableTable,
        Button,
        FloatingLabelTextInput,
        LabelledSeparator,
        DropdownSelect
    }
}
</script>

<style lang='less'>
@import '~@/styles/main.less';

#accounts-management-view
{
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    margin-top: calc(var(--theme-status-bar-height) + 2vh);
    margin-bottom: calc(var(--theme-horizontal-menu-height) + 2vh);
    padding: 0 2.5vw;

    .OnDesktop({
        border-left: var(--theme-vertical-menu-total-vertical-space) solid transparent;
    });
    .OnLarge({
        margin-left: 5%;
        margin-right: 5%;
    });

    #add-accounts-zone
    {
        display: flex;
        flex-direction: column;
        width: 100%;

        .OnTablet({ flex-direction: row });

        #create-account-btn
        {
            width: 100%;
            box-shadow: var(--theme-box-shadow-sharp);

            .OnTablet({
                height: 6vh;
                margin-top: auto;
                margin-bottom: auto;
            })
        }

        .labelled-separator
        {
            .OnTablet({ width: 25%; });
            .OnDesktop({ width: 70%; });
        }

        #add-existing-account
        {
            width: 90%;
            background-color: var(--theme-content-background-color);
            padding: 4% 5% 5% 5%;
            border-radius: 8px;
            box-shadow: var(--theme-box-shadow-soft);

            .OnTablet({
                width: 110%;
                padding: 2% 3% 3% 3%;
            });

            .OnDesktop({
                width: 95%;
                padding: 1.5% 2.5%;
            });
            
            .title
            {
                font-size: 4vw;

                .OnTablet({ font-size: 2vw; });

                .OnDesktop({ font-size: 0.9vw; });
            }

            #input-controls
            {
                display: flex;
                flex-direction: row;
                margin-top: 2vh;

                .OnTablet({ margin-top: 1vh; });
                .OnDesktop({ margin-top: 2vh; });

                .button
                {
                    height: 12vw;
                    padding: 0px 2vw;
                    margin-left: 10px;

                    .OnTablet({ height: 4vh; });
                }

                .floating-label-text-input
                {
                    width: 100%;
                    height: calc(12vw - 4px);

                    .OnTablet({
                        height: calc(4vh - 4px);
                        font-size: 2vw;
                    });

                    .OnDesktop({ font-size: 0.9vw; });
                }
            }
        }
    }

    .editable-table
    {
        width: 100%;
        max-height: 70vh;
        margin-top: 3vh;

        .OnDesktop({
            margin-top: 5vh;
            width: 98%;
        });

        table, thead, tr, th
        {
            &:first-of-type
            {
                .OnDesktop({ padding-right: 50px; });
            }
        }

        .row.active
        {
            .floating-label-text-input:not(.active)
            {
                border-color: var(--theme-border-color-strong);
            }
        }

        .cell
        {
            min-width: 80px;

            .button
            {
                width: 100%;
            }

            .floating-label-text-input
            {
                width: 200px;
                height: 38px;
            }
        }
    }

    #save-btn
    {
        margin-top: 10px;
        width: 100%;
    }
}
</style>



// WEBPACK FOOTER //
// src/views/AccountsManagement.vue